import React, { useState, useRef } from "react";
import "./contentpage.css";

import BoxWithShadow from "../components/BoxWithShadow";

import LandingSection from "./LandingSection";
import Question from "../components/Question";
import FloatingPageNav from "../components/FloatingPageNav";
function IslamicTradePage3() {
  const contentRef = useRef(null);
  const titleText = "Terms of Trade (Shuroot fil-Bay’)";
  const links = {
    prev: "/islamic-trade-page-2",
    index: "/index",
    next: "/islamic-trade-page-4",
  };
  const questions = [
    {
      text: "1. What is the primary distinction between conditions (shuroot al bayʿ) and terms(shuroot fil bayʿ) (shart) in an Islamic contract?",
      options: [
        {
          value: "A",
          label:
            "A. Conditions are derived from the Qur'an and Sunnah, while terms are mutually agreed upon by contracting parties.",
        },
        {
          value: "B",
          label:
            "B. Conditions can be waived by mutual agreement, whereas terms cannot be modified.",
        },
        {
          value: "C",
          label:
            "C. Conditions are always optional, while terms are mandatory for a valid contract.",
        },
        {
          value: "D",
          label:
            "D. Terms must always be explicitly mentioned in the Qur'an and Sunnah.",
        },
      ],
      correct: "A",
    },
    {
      text: "2. According to Islamic trade principles, when does a contract become void?",
      options: [
        {
          value: "A",
          label: "A. When a term is not fulfilled by one of the parties.",
        },
        {
          value: "B",
          label: "B.  When the buyer requests a home delivery clause.",
        },
        {
          value: "C",
          label: "C. When an essential condition of the contract is not met.",
        },
        {
          value: "D",
          label: "D. When both parties mutually agree to modify a term.",
        },
      ],
      correct: "C",
    },
    {
      text: "3. What is the Islamic principle that prohibits harm or unfair advantage in trade?",
      options: [
        {
          value: "A",
          label: "A. Charging interest (riba) on deferred payments.",
        },
        {
          value: "B",
          label: "B. Imposing excessive penalties on late payments.",
        },
        {
          value: "C",
          label:
            "C.  Agreeing to installment payments with a fixed price upfront.",
        },
        {
          value: "D",
          label:
            "D. Selling an item while retaining full ownership rights indefinitely.",
        },
      ],
      correct: "C",
    },
    {
      text: "4. According to Islamic trade principles, which of the following statements is true?",
      options: [
        {
          value: "A",
          label:
            "A. A contract is valid even if it contains an exploitative term.",
        },
        {
          value: "B",
          label:
            "B. Terms in a contract must always align with fairness and justice.",
        },
        {
          value: "C",
          label:
            "C. Conditions in a contract can be waived if both parties agree.",
        },
        {
          value: "D",
          label:
            "D.  Any additional term in a contract automatically invalidates it.",
        },
      ],
      correct: "B",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={3}
        timerMinutes={60}
        currentLevel={1}
      />
      <p className="content" ref={contentRef}>
        <br />
        <br /> Welcome to <b>Day 3</b>! As we continue our exploration of
        Islamic commercial ethics, Day three brings us to an essential area of
        everyday transactions i.e the terms of trade. These are the optional
        clauses or stipulations parties agree to within a contract to protect
        their interests and clarify responsibilities. In Islamic law, such terms
        are permitted as long as they do not contradict the spirit and rulings
        of Shari‘ah. Understanding these terms empowers both buyer and seller to
        engage in fair, flexible, and transparent trade agreements, guided by
        ethical boundaries.
        <br />
        <br />
        In commerce conducted in accordance with the guidelines of Islam, a term
        (shart fil-Bay’) refers to an additional stipulation imposed by one of
        the contracting parties beyond the fundamental conditions of the
        contract. While conditions (shuroot) are prescribed by the Qur'an and
        Sunnah, terms are mutually agreed upon by the involved parties. These
        terms add flexibility to transactions while ensuring compliance with
        Islamic legal principles.
        <br />
        For instance, in a house sale, the buyer and seller may mutually agree
        that the payment will be deferred for six months. This stipulation is
        not explicitly mentioned in the Qur’an or Sunnah but is considered valid
        as long as it aligns with the principles of fairness and justice. Such
        flexibility allows parties to structure transactions based on their
        needs while maintaining adherence to Islamic ethics.
        <br />
        Islamic trade principles ensure that while flexibility exists,
        transactions remain fair and free from exploitation. <br />
        The Prophet Muhammad (peace be upon him) emphasized fairness in trade,
        stating:
        <br />
        "The seller and the buyer have the right to keep or return goods as long
        as they have not parted or till they part; and if both the parties spoke
        the truth and described the defects and qualities (of the goods), then
        they would be blessed in their transaction, and if they told lies or hid
        something, then the blessings of their transaction would be lost."{" "}
        <i>
          <a href="https://sunnah.com/bukhari:2079" target="blank">
            (Sahih Al-Bukhari, Hadith 2079)
          </a>
        </i>
        <br />
        This hadith highlights the importance of mutual consent and transparency
        in business dealings. Terms should always align with the overarching
        principles of fairness, mutual agreement, and ethical conduct.
        <br />
        <br />
        <b>Difference Between Conditions and Terms</b>
        <br />
        <br />
        To understand the distinction between conditions(shuroot al bay') and
        terms(shuroot fil bay'), consider the following points:
        <br />
        <br />
        1) Conditions are deduced from the Qur'an and Sunnah expilicitly, while
        terms are agreed upon by the contracting parties.
        <br />
        <li>
          Conditions are mandatory elements of a contract as defined in Islamic
          law. For example, in a valid sale, ownership must be transferred, and
          the object of sale must be lawful.
        </li>
        <li>
          Terms, on the other hand, are additional stipulations set by the
          parties. For instance, a buyer may request a home delivery clause in a
          contract while ordering a product from an e-commerce website.
        </li>
        <br />
        <br />
        2) A contract's validity depends on fulfilling conditions, whereas terms
        affect its continuity. <br />
        <li>
          If an essential condition of a contract is not met, the contract
          becomes void. For example, selling something that is not owned by the
          seller renders the contract invalid.
        </li>
        <li>
          However, if a term is violated, it does not necessarily nullify the
          contract but may lead to renegotiation or dispute resolution.
        </li>
        <br />
        3) Conditions are obligatory, whereas terms may be waived if both
        parties agree.
        <br />
        <li>
          In a sale contract, ownership transfer is an obligatory condition that
          cannot be waived.
        </li>
        <li>
          However, a buyer may waive an agreed term, such as a warranty service,
          if both parties mutually agree.
        </li>
        <br />
        <br />
        <b>Islamic Principles Governing Trade Terms</b>
        <br />
        <br />
        Terms must align with Islamic principles of fairness and justice. Any
        term that contradicts Islamic ethical principles is automatically
        invalid. For example, a term that imposes excessive penalties for late
        payment would be considered unjust and prohibited in Islamic law.
        <br /> The Qur'an provides clear guidance on business ethics: <br />
        "O you who believe! Do not consume one another’s wealth unjustly, but
        only [do business] by mutual consent."
        <i>
          <a href="https://quran.com/4/29" target="blank">
            {" "}
            (Surah An-Nisa 4:29)
          </a>
        </i>
        <br /> This verse establishes the principle that business transactions
        must be free from coercion, deceit, and exploitation. Any terms in a
        contract that violate this principle are deemed invalid.
        <br />
        Additionally, the principle of <i>"La Darar wa la Dirar"</i> (No harm,
        no reciprocating harm) is fundamental in Islamic trade. Any term that
        causes harm or unfair advantage is not permissible.
        <br />
        <br />
        <b>Types of Valid Terms in Trade</b>
        <br />
        <br />
        Islamic jurisprudence allows certain types of contractual terms,
        provided they do not contradict Islamic teachings. Some of the common
        valid terms include:
      </p>
      <table className="trade-table">
        <tr>
          <th>Valid Terms in a Trade </th>
        </tr>
        <tr>
          <td>Mortgage (Rahn)</td>
        </tr>
        <tr>
          <td>Deferred Payment (Ta'jīl ath-Thaman)</td>
        </tr>
        <tr>
          <td>Delivery Terms</td>
        </tr>
        <tr>
          <td>Service Warranty</td>
        </tr>
        <tr>
          <td>Installment Payments</td>
        </tr>
      </table>
      <p className="content">
        <br />
        <br />
        <b>1) Mortgage (Rahn)</b> <br />
        <br />
        One of the most widely accepted contractual terms in Islamic trade is
        the concept of mortgage, or Rahn. Rahn allows a buyer to provide
        collateral to secure the payment of a debt or obligation. This mechanism
        serves as a form of security for the seller and ensures that the
        transaction can proceed even if the buyer may not immediately have the
        full payment. In Islamic jurisprudence, the collateral must be something
        valuable, tangible, and owned by the buyer. For example, if a person
        wishes to buy a house but does not have the full amount immediately,
        they can offer another asset as security until the payment is completed.
        <br />
        Rahn protects both parties. The seller gains assurance that their
        property or asset is secured, while the buyer benefits from the
        opportunity to complete the purchase without being unfairly burdened.
        This concept is rooted in Islamic teachings on justice and fairness. The
        Quran reminds us of the importance of fulfilling contracts and
        agreements:
        <br />
        “O believers! Honour your obligations. All grazing livestock has been
        made lawful to you except what is hereby announced to you and hunting
        while on pilgrimage. Indeed, Allah commands what He wills.”
        <a href="https://quran.com/5/1" target="blank">
          (Surah Al-Ma’idah, 5:1)
        </a>
        <br />
        Mortgage or Rahn is not just limited to real estate. It can apply to
        business transactions, trade goods, or valuable assets. For example, a
        merchant may provide jewelry or machinery as collateral while purchasing
        raw materials. It is essential that the collateral is legally owned by
        the buyer, is clearly defined, and is not subject to deception or
        uncertainty. This ensures that the contract remains valid under Islamic
        law, upholds transparency, and prevents disputes. The practical benefit
        of Rahn is significant. It encourages responsible trade, reduces the
        risk of default, and promotes confidence in commercial dealings. In
        essence, Rahn is a tool that balances security with ethical
        responsibility.
        <br />
        <br />
        <b>2) Deferred Payment (Ta'jīl ath-Thaman)</b>
        <br />
        <br /> Another essential term in Islamic trade is Deferred Payment, also
        known as Ta'jīl ath-Thaman. This term allows the buyer to postpone
        payment for a fixed period while still taking possession of the goods or
        services. Deferred payment is particularly useful in transactions where
        the buyer requires time to gather funds or manage cash flow.
        <br />
        A common example is the purchase of a car. A buyer may agree to buy a
        car today but pay for it after twelve months in months. The total price
        must be agreed upon upfront, and no additional interest or penalties can
        be charged, as this would constitute riba, which is explicitly
        prohibited in Islam. As is mentioned in the hadith :
        <br />
        Jabir (may Allah be pleased him) said that Allah's Messenger (peace and
        blessings be upon him) cursed the accepter of interest and its payer,
        and one who records it, and the two witnesses, and he said: "They are
        all equal.""
        <a href="https://sunnah.com/muslim:1598" target="blank">
          (Sahih Muslim, Hadith 1598)
        </a>
        <br />
        Deferred payment encourages trade by providing flexibility while
        maintaining ethical integrity. It ensures that both parties have a clear
        understanding of their rights and obligations. The seller knows exactly
        what amount they will receive, and the buyer knows the total payment
        without hidden charges or interest.
        <br />
        To ensure compliance with Islamic law, the terms of deferred payment
        must be transparent. The payment schedule should be clearly defined, and
        both parties must willingly agree. For example, a person purchasing
        furniture might agree to pay after four months. The final price is
        fixed, and any discounts or fees are disclosed upfront. Such clarity
        ensures that the transaction is free from uncertainty and manipulation,
        fulfilling the Islamic principle of honesty in trade. Deferred payment
        therefore promotes ethical commerce, financial flexibility, and mutual
        trust between buyers and sellers.
        <br />
        <br />
        <b>3) Delivery Terms</b> <br />
        <br />
        The third example of a valid contractual term is related to delivery of
        goods. In modern commerce, delivery terms are crucial, especially in
        online transactions, wholesale trade, and supply chain agreements. A
        seller may agree to deliver goods to the buyer’s location, either free
        of charge or at an agreed-upon cost. The terms must be clear to prevent
        disputes, and the seller must honor the agreement as a demonstration of
        trustworthiness and fairness.
        <br />
        <br />
        For instance, an online retailer may sell a product and provide free
        home delivery within a week. The buyer must receive the goods in the
        condition promised, and any failure to deliver as per the agreed terms
        could render the transaction invalid or liable for rectification.
        Islamic law emphasizes transparency and fulfillment of obligations.
        Allah says in the Quran:
        <br /> “Give full measure and weight with justice.”
        <a href="https://quran.com/6/152" target="blank">
          (Surah Al-An’am, 6:152)
        </a>
        <br />
        Delivery terms are also relevant in international trade. When goods are
        shipped across borders, contracts specify whether the seller bears the
        shipping cost, insurance, or risks during transit. These terms must be
        clear and agreed upon, as ambiguity could create gharar, which is
        prohibited in Islam. A well-defined delivery term ensures that the buyer
        receives the product, the seller is protected, and both parties can
        execute the transaction with confidence. This type of contractual
        clarity fosters trust, prevents disputes, and encourages a fair market
        environment, reflecting the ethical values that Islam upholds in
        commerce.
        <br />
        <br />
        <b>4) Service Warranty</b> <br />
        <br />
        Service warranty is another important term recognized in Islamic trade.
        A warranty ensures that the seller guarantees the repair or replacement
        of goods for a specified period after purchase. This term is valid as
        long as it is clearly defined, voluntary, and does not involve
        deception.
        <br />
        For example, a seller may provide a one-year warranty on a refrigerator.
        This ensures that if the appliance malfunctions within the warranty
        period, the buyer can request repair or replacement. Such a term
        protects the buyer, encourages accountability, and demonstrates ethical
        business practices. The Prophet Muhammad (peace be upon him) emphasized
        honesty and trustworthiness in trade: <br />
        “The truthful, trustworthy merchant is with the Prophets, the truthful,
        and the martyrs”
        <a href="https://sunnah.com/tirmidhi:1209" target="blank">
          {" "}
          (Sunan At-Tirmidhi, Hadith 1209)
        </a>
        <br />
        Service warranties are also extended to electronics, vehicles, and other
        high-value goods. The warranty period and scope of coverage must be
        agreed upon upfront. If a seller promises repair services but fails to
        deliver, they violate the terms of the contract, which is ethically and
        legally impermissible in Islam. Warranties provide security, reduce
        disputes, and encourage both parties to act responsibly. They also
        instill confidence in buyers, which strengthens commercial activity.
        <br />
        <br />
        <b>5) Installment Payments</b>
        <br />
        <br />
        Finally, installment payments are a widely used term in modern trade.
        This arrangement allows the buyer to pay for goods or services over a
        set period in multiple installments. Installments are permissible as
        long as the final price is fixed, and no interest is charged. Islamic
        trade principles require that all terms are transparent and agreed upon
        to avoid riba and gharar.
        <br />
        For example, a person may purchase a washing machine and agree to pay
        over six months. The total price is clearly specified, and the seller
        cannot demand additional payments beyond the agreed amount. Installment
        payments facilitate commerce, particularly for high-value items, and
        provide financial accessibility to buyers without violating Islamic law.
        This mechanism also encourages careful financial planning and
        responsible spending.
        <br />
        The Installments we are referring to are distinct from conventional
        loans because they are based on ethical trade rather than interest.
        Buyers receive goods in exchange for a clear and agreed-upon payment
        schedule, reflecting fairness and honesty. Sellers benefit from
        increased sales, and buyers benefit from manageable payment terms. Both
        parties engage in commerce that aligns with Islamic principles,
        promoting justice, transparency, and mutual satisfaction.
        <br />
        <br />
        <BoxWithShadow>
          <strong>IMPORTANT</strong>
          <br />
          Even though buying goods and services with deferred payments or on
          installements is permissible, a Muslim is encouraged to live a life
          within his means and spend in moderation, falling prey to consumerism
          and spending with extravagence is highly discouraged at the very least
          in Islam.
        </BoxWithShadow>
        <br />
        <b>Invalid and Unlawful Terms</b>
        <br />
        <br />
        1) Conditions that Contradict the Nature of the Contract <br />
        Example: Selling an item while retaining full ownership rights. If a
        person sells a house but insists on retaining the authority to occupy it
        indefinitely, such a term is impermissible.
        <br />
        <br /> 2) Unfair Terms That Exploit the Buyer or Seller <br />
        Terms that impose undue hardship or excessive penalties are considered
        unjust. Example: A seller imposing a penalty on a buyer for late
        payment. <br />
        Thus, any term that goes against Islamic legal principles is
        automatically nullified.
        <br />
        <br />
        Understanding the role of terms in Islamic trade is crucial for ensuring
        transactions remain just and compliant with Islamic law. While
        conditions are fundamental and non-negotiable, terms provide flexibility
        to accommodate the needs of both parties. However, any term that
        contradicts Islamic ethics or exploits either party is deemed invalid.
        By adhering to the Qur'an, Sunnah, and principles of fairness, trade in
        Islam remains a means of mutual benefit and ethical economic activity.
        The key takeaway is that while terms offer room for negotiation, they
        must always adhere to the overarching principles of Islamic fairness and
        justice.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today, we examined the role and rules surrounding the terms of trade
        (shurūt fil-Bay') in Islamic contracts: <br />
        <ul>
          <li>
            {" "}
            The definition of terms and how they differ from the core pillars of
            a sale.
          </li>
          <li>
            The legal status of stipulated terms: which are valid, which are
            invalid, and which invalidate the entire contract.{" "}
          </li>
          <li>
            Examples of valid terms: such as delivery time, down payments, or
            buyer’s conditions on quality or inspection.
          </li>
          <li>
            Examples of invalid terms: such as conditions that contradict the
            essence of the sale or impose unjust burdens.
          </li>
        </ul>
        <b> Coming Up Next</b>
        <br />
        <br />
        Next, we will explore the concept of Khiyār, the right of withdrawal or
        option in Islamic trade contracts. This principle reflects the
        flexibility and justice inherent in Shari‘ah, allowing parties to a
        transaction the ability to reconsider or cancel a deal under certain
        circumstances. Khiyār serves as a protective mechanism in cases
        involving hidden defects, deception, or delayed decision-making. It
        ensures that neither party is unfairly bound to a transaction that lacks
        full transparency or mutual satisfaction. By studying Khiyār, we build
        on today’s discussion of fairness and consent in trade, gaining a deeper
        understanding of how Islamic law upholds both the freedom and the
        accountability of contractual agreements.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default IslamicTradePage3;
